import { cn } from '@/lib/cn';

/**
 * Clockit brand (sprint 12): the clock-and-stopwatch mark in the accent colour, the "Clockit" wordmark
 * (Outfit 600) and the "by Skaleminds." credit line (Poppins). The mark's geometry is copied verbatim
 * from the branding reference and kept in step with public/favicon.svg by src/test/favicon.test.ts.
 *
 * The wordmark is a brand mark, not a heading: it is rendered with spans only, never h1–h6, so the
 * weight-400 heading rule does not apply (documented exception). Never wrap it in a link (public pages).
 * Not a lucide icon, so the global 1.5 stroke rule does not apply; the mark is decorative.
 */
export function Logo({ size = 'md', className }: { size?: 'md' | 'lg'; className?: string }): JSX.Element {
  const mark = size === 'lg' ? 40 : 30;
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span className="shrink-0 text-primary">
        <svg width={mark} height={mark} viewBox="-4 -8 68 68" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path
            d="M30.0072 3.0293C32.6641 3.0293 35.2326 3.41538 37.6593 4.13436C38.2446 4.30774 38.4878 4.99133 38.1668 5.51047C37.9279 5.89673 37.45 6.05845 37.0194 5.9143C31.34 4.01321 25.2466 4.43707 20.2276 7.62919C10.6202 13.7408 8.53234 27.72 15.5721 38.7894C22.6133 49.858 36.1604 53.8913 45.7677 47.7797C48.2524 46.1994 50.2341 44.093 51.6849 41.6467C51.9909 41.1309 52.6181 40.8876 53.1795 41.0987C53.83 41.3432 54.1341 42.0934 53.8059 42.706C49.2569 51.1973 40.2955 56.9861 30.0072 56.9861C15.131 56.9861 3.02881 44.8832 3.02881 30.0077C3.02881 15.1322 15.131 3.0293 30.0072 3.0293Z"
            fill="currentColor"
          />
          <path
            d="M42.3086 7.15626C42.6108 6.66758 43.2434 6.49799 43.7375 6.79128C51.6624 11.4961 56.9855 20.1421 56.9855 30.0076C56.9855 32.1536 56.7336 34.2418 56.2578 36.2443C56.1027 36.8972 55.4044 37.2467 54.7762 37.0105C54.2201 36.8016 53.918 36.2041 54.0473 35.6243C55.3921 29.5944 54.2713 22.6731 50.4224 16.6216C48.3217 13.3191 45.642 10.6429 42.6607 8.67541C42.1564 8.34265 41.9909 7.6701 42.3086 7.15626Z"
            fill="currentColor"
          />
          <g stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
            <line x1="39.55" y1="37.93" x2="41.8" y2="41.82" />
            <line x1="30.06" y1="39.18" x2="28.9" y2="43.52" />
            <line x1="22.48" y1="33.35" x2="18.58" y2="35.6" />
            <line x1="21.23" y1="23.86" x2="16.88" y2="22.7" />
            <line x1="27.05" y1="16.28" x2="24.8" y2="12.38" />
          </g>
          <g stroke="currentColor" strokeWidth="4" strokeLinecap="round">
            <line x1="33.3" y1="27.1" x2="39.8" y2="23.35" />
            <line x1="33.3" y1="27.1" x2="33.3" y2="16.1" />
          </g>
          <circle cx="33.3" cy="27.1" r="3" fill="currentColor" />
          <g fill="currentColor" transform="rotate(5 30 30)">
            <rect x="28" y="-1" width="4" height="5.5" />
            <rect x="23.5" y="-6.5" width="13" height="6" rx="3" />
          </g>
        </svg>
      </span>
      <span className="flex flex-col leading-[1.05]">
        <span className={cn('font-brand font-semibold tracking-[-0.015em] text-brand-wordmark', size === 'lg' ? 'text-[28px]' : 'text-[22px]')}>Clockit</span>
        <span className={cn('font-credit text-brand-credit mt-0.5 tracking-[-0.01em]', size === 'lg' ? 'text-xs' : 'text-[10px]')}>
          by <span className="font-normal">Skale</span>
          <span className="font-semibold">minds</span>
          <span className="font-bold text-brand-dot">.</span>
        </span>
      </span>
    </span>
  );
}
